import { referenceDisplay } from '@seen/core/display'
import type { PhoneCommunication } from '@seen/core/models/communications/PhoneCommunication'
import { compact } from 'lodash-es'
import { type JSX, type MouseEvent, useMemo } from 'react'
import { Loader } from '#src/components/core/Loader.tsx'
import { RelativeDateTimeDisplay } from '#src/components/display/RelativeDateTimeDisplay.tsx'
import type { ColumnConfig } from '#src/components/fhir-table/FhirMRTTable.constants.tsx'
import { ReactTable } from '#src/components/fhir-table/ReactTable.tsx'

interface PhoneCallsTableProps {
  patientId?: string
  onClickRow?: (call: PhoneCommunication, event: MouseEvent) => void
  data: PhoneCommunication[] | undefined
  isLoading: boolean
  error: Error | null
}

export function PhoneCallsTable({
  onClickRow,
  data,
  isLoading,
  error,
}: PhoneCallsTableProps): JSX.Element {
  const columnConfigs = useMemo<ColumnConfig<PhoneCommunication>[]>(
    () =>
      compact([
        {
          id: 'startTime',
          headerText: 'Start Time',
          render: (call) => <RelativeDateTimeDisplay value={call.startTime} />,
          valueType: 'date' as const,
          getSortFilterValue: (call) =>
            call.startTime ? new Date(call.startTime) : undefined,
          filteringType: 'date-range',
          enableSorting: true,
        },
        {
          id: 'partOf',
          headerText: 'Related To',
          render: (call) => (
            <span>{referenceDisplay(call.partOf?.[0]) ?? ''}</span>
          ),
          valueType: 'text' as const,
          getSortFilterValue: (call) =>
            referenceDisplay(call.partOf?.[0]) ?? '',
          filteringType: 'text' as const,
          enableSorting: true,
        },
        {
          id: 'callerNumber',
          headerText: 'Caller',
          render: (call) => <span>{call.callerNumber}</span>,
          valueType: 'text' as const,
          getSortFilterValue: (call) => call.callerNumber ?? '',
          filteringType: 'text' as const,
          enableSorting: true,
        },
        {
          id: 'receiverNumber',
          headerText: 'Receiver',
          render: (call) => <span>{call.receiverNumber}</span>,
          valueType: 'text' as const,
          getSortFilterValue: (call) => call.receiverNumber ?? '',
          filteringType: 'text' as const,
          enableSorting: true,
        },
        {
          id: 'direction',
          headerText: 'Direction',
          render: (call) => (
            <span>{call.isOutbound ? 'Outbound' : 'Inbound'}</span>
          ),
          valueType: 'text' as const,
          getSortFilterValue: (call) =>
            call.isOutbound ? 'Outbound' : 'Inbound',
          filteringType: 'text' as const,
          enableSorting: true,
        },
        {
          id: 'messageSummary',
          headerText: 'Summary',
          render: (call) => (
            <span className="line-clamp-1">{call.messageSummary}</span>
          ),
          valueType: 'text' as const,
          getSortFilterValue: (call) => call.messageSummary ?? '',
          filteringType: 'text' as const,
          enableSorting: true,
        },
      ]),
    []
  )

  if (error) {
    return <div>Error: {error.message}</div>
  }

  if (isLoading) {
    return <Loader />
  }

  return (
    <ReactTable
      data={data ?? []}
      columnConfigs={columnConfigs}
      tableOptions={{
        mantineTableBodyRowProps: ({ row }) => ({
          onClick: (event) => onClickRow?.(row.original, event),
          className: 'cursor-pointer',
        }),
      }}
    />
  )
}
